import React, { Component, createRef } from "react";
import styled, { keyframes, css } from "styled-components";
import _ from "lodash";
import {
    Popover,
    Button
} from "@material-ui/core";
import type { PopoverOrigin } from "@material-ui/core/Popover";

import { Icon } from "js/Components/Icon";
import { TaskItem, Task } from "./TaskItem";
import { HoverHighlight } from "./HoverHighlight";
import QuickStartController from "js/controllers/QuickStartController";
import { QuickStartTask } from "common/constants";
import { calculateBoundingBox } from "./utils/domUtils";
import { CompletedStep } from "./CompletedStep";
import TaskRendererFactory, { TaskRendererFactoryProps } from "./TaskRendererFactory";
import PresentationEditorController from "../../PresentationEditorController";
import { POPOVER_TASK_CONFIGS, TASK_CONFIGS, VIDEO_TASK_CONFIGS } from "./taskConfig";

const ButtonContent = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #FFF;
    text-align: center;
    font-family: "Source Sans Pro";
    text-transform: none;
`;

const StyledButton = styled(Button)`
    &&& {
        background-color: #8D31E6;
        color: #FFF;
        padding: 6px 16px;
        border-radius: 100px;
        position: absolute;
        bottom: 24px;
        right: 82px;
        z-index: 1000;

        :hover {
            background-color: #7427BE;
        }
    }
`;

const Title = styled.div`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: #FFF;
    padding: 16px;
    background: linear-gradient(90deg, #4F46E5 0%, #9333EA 100%);
`;

const TaskList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px;
`;

// Animation constants
const ANIMATION_DURATION_MS = 800;
const POPOVER_OFFSET_ABOVE_BUTTON = 8;

// Slide down from above button and fade in
const slideUpFadeIn = keyframes`
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

// Slide up to above button and fade out
const slideDownFadeOut = keyframes`
    from {
        opacity: 1;
        transform: translateY(0);
    }
    to {
        opacity: 0;
        transform: translateY(8px);
    }
`;

const NonBlockingPopover = styled(Popover)<{ $isClosing: boolean }>`
    &&& {
        pointer-events: none;
        
        .MuiPaper-root {
            pointer-events: auto;
            transform-origin: bottom center;
            ${css`animation: ${slideUpFadeIn} ${ANIMATION_DURATION_MS}ms ease-out forwards;`}
        }
        
        ${props => props.$isClosing && css`
            .MuiPaper-root {
                animation: ${slideDownFadeOut} ${ANIMATION_DURATION_MS}ms ease-out forwards;
            }
        `}
    }
`;

interface QuickStartMenuProps {
    onClose?: () => void;
    quickStartTasks?: Record<string, boolean> | null;
    forceOpen?: boolean;
}

interface ActivePopover {
    taskKey: string;
    state: {
        position: { top: number; left: number };
        transformOrigin: PopoverOrigin;
    };
}

interface QuickStartMenuState {
    popover: ActivePopover | null;
    showCompletedStep: boolean;
    hoveredTaskKey: string | null;
    remountKey: number; // Increments to force remount of HoverHighlight components
    triggerTaskKey: string | null; // Task key for active video (set on mount or when task is clicked)
    isClosing: boolean;
    popoverPosition: { top: number; left: number } | null; // Store popover position (8px above button)
}

const TASK_ORDER = [
    QuickStartTask.DISCOVER_SMART_SLIDES,
    QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE,
    QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL,
    QuickStartTask.PERSONALIZE_YOUR_THEME,
    QuickStartTask.PRESENT_LIKE_A_PRO
];

class QuickStartMenu extends Component<QuickStartMenuProps, QuickStartMenuState> {
    taskItemRefs: Map<string, React.RefObject<HTMLDivElement>> = new Map();
    buttonRef = createRef<HTMLButtonElement>();
    debouncedHandleToggle: _.DebouncedFunc<() => void>;

    constructor(props: QuickStartMenuProps) {
        super(props);
        this.state = {
            popover: null,
            showCompletedStep: false,
            hoveredTaskKey: null,
            remountKey: 0,
            triggerTaskKey: null,
            isClosing: false,
            popoverPosition: null
        };

        // Throttle handleToggle to prevent rapid clicks from messing up the UI during animations
        this.debouncedHandleToggle = _.throttle(() => {
            this.handleToggle();
        }, ANIMATION_DURATION_MS + 100, {
            leading: true,
            trailing: false
        });

        QuickStartController.taskKeys.forEach(key => {
            // Create refs for each task that should be shown in the menu
            this.taskItemRefs.set(key, createRef<HTMLDivElement>());
        });
    }

    componentDidMount() {
        const taskStatus = QuickStartController.getTaskStatus(QuickStartTask.DISCOVER_SMART_SLIDES);
        const hasBeenShown = localStorage.getItem("discoverSmartSlidesTaskShown") !== null;

        if (!taskStatus && !hasBeenShown) {
            // Delay the showing of the popover to avoid opening it too early when the presentation is still loading
            setTimeout(() => {
                this.setState({ triggerTaskKey: QuickStartTask.DISCOVER_SMART_SLIDES });
                localStorage.setItem("discoverSmartSlidesTaskShown", "true");
            }, 1600);
        }
    }

    componentDidUpdate(prevProps: QuickStartMenuProps, prevState: QuickStartMenuState) {
        this.handleForceOpenChange(prevProps, prevState);
    }

    componentWillUnmount() {
        this.debouncedHandleToggle.cancel();
    }

    handleForceOpenChange = (prevProps: QuickStartMenuProps, prevState: QuickStartMenuState) => {
        const { forceOpen } = this.props;

        if (forceOpen && !prevProps.forceOpen && this.buttonRef.current) {
            setTimeout(() => {
                QuickStartController.setForceOpen(false);
                this.setState({
                    popover: null,
                    showCompletedStep: false,
                    hoveredTaskKey: null,
                    triggerTaskKey: null,
                    popoverPosition: this.getPopoverPosition(),
                    remountKey: prevState.remountKey + 1
                });
            }, 100);
        }
    };

    handleToggle = () => {
        const { isClosing, popoverPosition } = this.state;
        // Prevent toggling during closing animation
        if (isClosing) {
            return;
        }

        if (popoverPosition) {
            this.handleClose();
        } else {
            const position = this.getPopoverPosition();
            this.setState(prevState => ({
                isClosing: false,
                popoverPosition: position,
                popover: null,
                hoveredTaskKey: null,
                remountKey: prevState.remountKey + 1
            }));
        }
    };

    handleClose = (_event?: React.MouseEvent<Document>, reason?: string) => {
        const { onClose } = this.props;

        if (reason === "backdropClick") {
            return;
        }

        this.setState({ isClosing: true });
        setTimeout(() => {
            this.setState({
                popover: null,
                hoveredTaskKey: null,
                isClosing: false,
                popoverPosition: null
            });
            onClose?.();
        }, ANIMATION_DURATION_MS);
    };

    showPopoverForTask = (taskKey: string) => {
        const config = POPOVER_TASK_CONFIGS[taskKey];
        if (!config || !config.popoverConfig || !config.highlightSelector) {
            return false;
        }

        const boundingBox = calculateBoundingBox(config.highlightSelector);
        if (!boundingBox) {
            return false;
        }

        const position = config.popoverConfig.position(boundingBox);
        this.setState({
            popover: {
                taskKey,
                state: {
                    position,
                    transformOrigin: config.popoverConfig.transformOrigin
                }
            }
        });

        return true;
    };

    handleTaskClick = async (task: Task) => {
        this.handleVideoClose();

        const config = TASK_CONFIGS[task.key];
        if (!config) return;

        if (config.type === "popover" && config.popoverConfig) {
            if (this.showPopoverForTask(task.key)) {
                return;
            }
        }

        if (task.key === QuickStartTask.ADD_YOUR_FIRST_SMART_SLIDE) {
            await PresentationEditorController.showAddSlideDialog();
            return;
        }

        if (config.type === "video" && config.videoUrl) {
            this.setState({
                triggerTaskKey: task.key,
                popover: null
            });
        }
    };

    handleThemePopoverClose = () => {
        this.setState({
            popover: null
        });

        this.checkAndShowCompletedStep();
    };

    handleVideoClose = async () => {
        this.setState({
            triggerTaskKey: null
        });

        // Use setTimeout to ensure state and props have updated
        setTimeout(() => {
            this.checkAndShowCompletedStep();
        }, 0);
    };

    handleTaskComplete = async () => {
        const { triggerTaskKey } = this.state;
        if (!triggerTaskKey) return;

        const isAlreadyDone = QuickStartController.getTaskStatus(triggerTaskKey);
        if (isAlreadyDone) return;

        await QuickStartController.updateTask(triggerTaskKey, true);
    };

    checkAndShowCompletedStep = () => {
        const { showCompletedStep } = this.state;

        // Check directly from QuickStartController to ensure we have the latest state
        const allDone = QuickStartController.areAllTasksDone();
        if (allDone && !showCompletedStep) {
            this.setState({
                showCompletedStep: true
            });
        }
    };

    handleCompletedStepClose = () => {
        this.setState({
            showCompletedStep: false,
            popover: null,
            popoverPosition: null
        });
    };

    closeAllPopovers = () => {
        this.setState({
            popover: null,
            triggerTaskKey: null
        });
    };

    handleTaskMouseEnter = (taskKey: string) => {
        if (taskKey === QuickStartTask.DISCOVER_SMART_SLIDES) {
            this.setState({ hoveredTaskKey: taskKey });
        }
    };

    handleTaskMouseLeave = (taskKey: string) => {
        if (taskKey === QuickStartTask.DISCOVER_SMART_SLIDES) {
            this.setState({ hoveredTaskKey: null });
        }
    };

    // This sets the order of the tasks in the quick start menu
    getTasks = (): Task[] => {
        const { quickStartTasks } = this.props;

        return TASK_ORDER.map(key => {
            const config = TASK_CONFIGS[key];
            return {
                title: config.title,
                key,
                done: quickStartTasks?.[key] || false
            };
        });
    };

    getTaskRendererProps = (): TaskRendererFactoryProps => {
        const { popover, hoveredTaskKey, triggerTaskKey } = this.state;

        if (popover) {
            return {
                type: "popover",
                taskKey: popover.taskKey,
                popoverState: popover.state,
                onPopoverClose: this.handleThemePopoverClose
            };
        }

        if (triggerTaskKey) {
            const config = VIDEO_TASK_CONFIGS[triggerTaskKey];
            if (config?.videoUrl) {
                return {
                    type: "full",
                    taskKey: triggerTaskKey,
                    videoUrl: config.videoUrl,
                    onVideoClose: () => {
                        if (!QuickStartController.areAllTasksDone()) {
                            // Every time we close the video, we reopen the quick start menu
                            QuickStartController.setForceOpen(true);
                        }
                        this.handleVideoClose();
                    },
                    onTaskComplete: this.handleTaskComplete
                };
            }
        }

        if (hoveredTaskKey) {
            const config = VIDEO_TASK_CONFIGS[hoveredTaskKey];
            if (config?.videoUrl) {
                return {
                    type: "preview",
                    taskKey: hoveredTaskKey,
                    videoUrl: config.videoUrl
                };
            }
        }

        return { type: "none" };
    };

    renderHoverHighlights = (tasks: Task[]) => {
        const { remountKey } = this.state;
        return tasks.map(task => {
            const config = POPOVER_TASK_CONFIGS[task.key];
            const taskItemRef = this.taskItemRefs.get(task.key);

            if (!config?.highlightSelector || !taskItemRef) {
                return null;
            }

            return (
                <HoverHighlight
                    key={`highlight-${task.key}-${remountKey}`}
                    target={config.highlightSelector}
                    hoverTriggerRef={taskItemRef}
                    noPadding={task.key === QuickStartTask.DIVE_DEEPER_WITH_THE_PANEL}
                />
            );
        });
    };

    renderTaskItems = (tasks: Task[]) => {
        return tasks.map(task => {
            const config = TASK_CONFIGS[task.key];
            if (!config) {
                return null;
            }

            return (
                <TaskItem
                    key={task.key}
                    task={task}
                    onClick={() => this.handleTaskClick(task)}
                    containerRef={this.taskItemRefs.get(task.key)}
                    onMouseEnter={() => this.handleTaskMouseEnter(task.key)}
                    onMouseLeave={() => this.handleTaskMouseLeave(task.key)}
                />
            );
        });
    };

    getPopoverPosition = (): { top: number; left: number } | null => {
        if (!this.buttonRef.current) {
            return null;
        }

        const buttonRect = this.buttonRef.current.getBoundingClientRect();
        return {
            top: buttonRect.top - POPOVER_OFFSET_ABOVE_BUTTON,
            left: buttonRect.right
        };
    };

    render() {
        const { quickStartTasks } = this.props;
        const { showCompletedStep, popover, triggerTaskKey, popoverPosition, isClosing } = this.state;

        if (_.isEmpty(quickStartTasks)) {
            return null;
        }

        if (showCompletedStep) {
            return (
                <CompletedStep
                    open={showCompletedStep}
                    onClose={this.handleCompletedStepClose}
                />
            );
        }

        const shouldHide = QuickStartController.areAllTasksDone() && !popover && !triggerTaskKey && !popoverPosition;
        if (shouldHide) {
            return null;
        }

        const tasks = this.getTasks();

        return (
            <>
                <StyledButton ref={this.buttonRef} onClick={this.debouncedHandleToggle} >
                    <ButtonContent>
                        <Icon color="#FFF" outlined large>rocket_launch</Icon>
                        Quick start
                        <Icon color="white" large>{popoverPosition ? "keyboard_arrow_down" : "keyboard_arrow_up" }</Icon>
                    </ButtonContent>
                </StyledButton>
                <NonBlockingPopover
                    open={!!popoverPosition || isClosing}
                    anchorReference="anchorPosition"
                    anchorPosition={popoverPosition || { top: 0, left: 0 }}
                    onClose={this.handleClose}
                    disableBackdropClick
                    disableEnforceFocus
                    disableAutoFocus
                    transformOrigin={{
                        vertical: "bottom",
                        horizontal: "right"
                    }}
                    transitionDuration={0}
                    $isClosing={isClosing}
                    PaperProps={{
                        style: {
                            width: 280,
                            background: "#F9F9F9",
                            borderRadius: 8,
                            boxShadow: "0 9px 37px 0 rgba(0, 0, 0, 0.20)",
                            border: "1px solid #222"
                        }
                    }}
                    disableRestoreFocus
                >
                    <Title>
                        Learn the Basics
                    </Title>
                    <TaskList>
                        {this.renderTaskItems(tasks)}
                    </TaskList>
                </NonBlockingPopover>
                {this.renderHoverHighlights(tasks)}
                {/* Task Renderer Factory - handles popover, preview, and full video */}
                <TaskRendererFactory {...this.getTaskRendererProps()} />
            </>
        );
    }
}

export default QuickStartController.withState(QuickStartMenu);

